import dayjs from 'dayjs'
import 'dayjs/locale/sl'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import isoWeek from 'dayjs/plugin/isoWeek'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import { CalendarDate } from '@internationalized/date'
import i18n from '@/libs/i18n'

dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.extend(isoWeek)
dayjs.extend(customParseFormat)

export const TZ = 'Europe/Ljubljana'

// Keep the dayjs global locale in sync with the app locale (called from the app store).
export const setDayjsLocale = (locale: string) => {
    dayjs.locale(locale)
}

// Initialize from the persisted locale ('sl' default).
setDayjsLocale(localStorage.getItem('locale') || 'sl')

export default dayjs

export const formatDate = (value: string | Date): string =>
    dayjs(value).format('DD. MM. YYYY')

export const formatTime = (value: string | Date): string =>
    dayjs(value).format('HH:mm')

export const formatDateTime = (value: string | Date): string =>
    dayjs(value).format('DD. MM. YYYY HH:mm')

// A span that ends on a later day (overnight / multi-day) names the end date, so
// "17:00–06:30" never reads as a same-day booking. An end at exactly the next midnight
// stays a same-day "–00:00".
export const formatTimeRange = (start: string, end: string): string => {
    const s = dayjs(start)
    const e = dayjs(end)
    const endsLater = e.isAfter(s.endOf('day')) && !e.isSame(s.add(1, 'day').startOf('day'))
    if (endsLater) return `${formatTime(start)} – ${e.format('DD. MM.')} ${formatTime(end)}`
    return `${formatTime(start)}–${formatTime(end)}`
}

export const formatWeekday = (value: string | Date): string =>
    dayjs(value).format('ddd')

// Integer minutes → "12 h 30 min" / "45 min" (report totals, api-contract §4 Reports).
export const formatMinutes = (minutes: number): string => {
    const t = i18n.global.t
    const total = Math.max(0, Math.round(minutes))
    const h = Math.floor(total / 60)
    const m = total % 60
    if (!h) return t('report.minutes_short', { m })
    if (!m) return t('report.hours_short', { h })
    return t('report.hours_minutes_short', { h, m })
}

// Locale-independent 24-hour time options ('HH:mm') for select-based time pickers —
// avoids native <input type="time"> rendering AM/PM in 12h browser locales.
export const timeOptions = (stepMin = 15): { label: string; value: string }[] => {
    const out: { label: string; value: string }[] = []
    for (let m = 0; m < 24 * 60; m += stepMin) {
        const label = `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`
        out.push({ label, value: label })
    }
    return out
}

// --- CalendarDate <-> ISO ('YYYY-MM-DD') helpers for UInputDate / UCalendar ---

export const calendarDateToISO = (
    date: { year: number; month: number; day: number } | null | undefined,
): string => {
    if (!date) return ''
    const mm = String(date.month).padStart(2, '0')
    const dd = String(date.day).padStart(2, '0')
    return `${date.year}-${mm}-${dd}`
}

export const isoToCalendarDate = (iso: string | null | undefined): CalendarDate | undefined => {
    if (!iso) return undefined
    const d = dayjs(iso)
    if (!d.isValid()) return undefined
    return new CalendarDate(d.year(), d.month() + 1, d.date())
}

export const todayCalendarDate = (): CalendarDate => {
    const d = dayjs()
    return new CalendarDate(d.year(), d.month() + 1, d.date())
}

// Compose an RFC3339-ish local instant from date ('YYYY-MM-DD') + time ('HH:mm').
export const composeInstant = (date: string, time: string): string =>
    dayjs(`${date} ${time}`, 'YYYY-MM-DD HH:mm').format()

// Relative time in Slovenian/English via i18n keys.
export const relativeTime = (value: string | Date): string => {
    const t = i18n.global.t
    const now = dayjs()
    const then = dayjs(value)
    const diffMin = now.diff(then, 'minute')
    if (diffMin < 1) return t('time.just_now')
    if (diffMin < 60) return t('time.min_ago', { n: diffMin })
    const diffHour = now.diff(then, 'hour')
    if (diffHour < 24) return t('time.hour_ago', { n: diffHour })
    const diffDay = now.diff(then, 'day')
    if (diffDay === 1) return t('time.yesterday')
    if (diffDay < 7) return t('time.days_ago', { n: diffDay })
    return formatDate(value)
}
